'use client';

import { Ref, useEffect, useRef } from 'react';
import { useLoadScript } from '@axo/shared/util/dom';
import { Locale } from '@axo/ui-core/i18n/@types/Locales.types';

export type ITrustpilot = {
  locale: Locale;
  templateId: string;
  businessId: string;
  link: string;
  height: string;
  width?: string;
  theme?: 'light';
  dataProps?: { [key: string]: string };
};

export const Trustpilot = ({
  locale,
  templateId,
  businessId,
  link,
  height,
  width = '100%',
  theme = 'light',
  dataProps = {},
}: ITrustpilot) => {
  const { isScriptLoaded, isScriptError } = useLoadScript(
    'trustpilot-widget-script',
    'https://widget.trustpilot.com/bootstrap/v5/tp.widget.bootstrap.min.js'
  );
  const ref = useRef(null);

  useEffect(() => {
    if (window.Trustpilot) {
      window.Trustpilot.loadFromElement(ref.current, true);
    }
  }, [isScriptLoaded, isScriptError]);

  const data = Object.entries(dataProps).reduce<{ [key: string]: string }>(
    (acc, [key, value]) => {
      acc[`data-${key}`] = value;
      return acc;
    },
    {}
  );

  if (isScriptError || !isScriptLoaded) {
    return null;
  }

  return (
    <div
      ref={ref}
      className="trustpilot-widget"
      data-locale={locale}
      data-template-id={templateId}
      data-businessunit-id={businessId}
      data-style-height={height}
      data-style-width={width}
      data-theme={theme}
      {...data}
    >
      <a href={link} target="_blank" rel="noreferrer">
        Trustpilot
      </a>
    </div>
  );
};

declare global {
  interface Window {
    Trustpilot: {
      loadFromElement: (ref: Ref<() => void>, load: boolean) => void;
    };
  }
}
